import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { useAppDispatch } from "@app/hooks";
import { setActiveEvent } from "@app/slices/wellContextSlice";
import { QueryState } from "@components/ui/QueryState";
import {
  useGetEventReportsQuery,
  useGetPlanVsActualQuery,
  useGetNptRollupQuery,
} from "@api/pApi";
import { useUpdateWellCoordsMutation } from "@api/mApi";
import { isGlobalAdmin } from "@api/brokerAuth";
import { fmtDepth, fmtMoney, fmtPct } from "@lib/format";
import type { WellView } from "@api/types";
import type { EventOut, MessageEventTypeKey } from "./eventType";
import { eventTypeKey } from "./eventType";
import { STATUS_KEY } from "./wellLabels";
import { NewEventButton } from "./NewEventButton";
import { WellboresSection } from "./WellboresSection";

/** Whether a 403 (admins-only) is buried in an RTK Query error. */
function isForbidden(err: unknown): boolean {
  return (
    !!err && typeof err === "object" && "status" in err && (err as { status: unknown }).status === 403
  );
}

/**
 * Well / Wellbore record hub. Header comes from Oxbo-M (the register); the
 * event/job selector and the three doorway cards each surface ONE real stat
 * from Oxbo-P (latest report day, AFE % spent, NPT %) — server-computed, shown
 * verbatim. Field-user-first, no ownership tint by default.
 */
export function WellRecord() {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { well, events, activeEvent, isLoading, isError } = useCurrentWell();

  if (isLoading || isError || !well) {
    return (
      <div>
        <div className="page-head">
          <h1>{t("nav.wellRecord")}</h1>
        </div>
        <div className="card">
          <QueryState isLoading={isLoading} isError={isError} isEmpty={!well}>
            <div className="empty-state">{t("well.selectPrompt")}</div>
          </QueryState>
        </div>
      </div>
    );
  }

  const evKey: MessageEventTypeKey | null = activeEvent ? eventTypeKey(activeEvent) : null;

  return (
    <div>
      <div className="page-head">
        <h1>{well.commonName}</h1>
        <p className="subtitle">
          {well.field} · {well.operator}
          {evKey ? ` · ${t(evKey)}` : ""}
        </p>
      </div>

      {/* Header — from Oxbo-M */}
      <div className="card" data-owner="m" style={{ marginBottom: 14 }}>
        <div className="kv-grid">
          <KV k={t("well.header.uwi")} v={well.uwi} mono />
          <KV k={t("well.header.api")} v={well.apiNo ?? "—"} mono />
          <KV k={t("well.header.field")} v={well.field} />
          <KV k={t("well.header.operator")} v={well.operator} />
          <KV k={t("well.header.formation")} v={well.formation ?? "—"} />
          <KV k={t("well.header.td")} v={fmtDepth(well.totalDepthM, locale)} mono />
          <KV
            k={t("well.header.status")}
            v={well.status ? t(STATUS_KEY[well.status]) : (well.statusRaw ?? "—")}
          />
        </div>
      </div>

      {/* Ubicación — well coordinates (from Oxbo-M), admin-editable */}
      <LocationCard well={well} />

      {/* Pozos perforados / Wellbores — OH + sidetracks (from Oxbo-M) */}
      <WellboresSection wellId={well.id} />

      {/* Event / job selector — from Oxbo-P. This record is the primary entry
          point for adding a job: an inviting prompt when the well has none, and a
          "+ Agregar evento" action in the selector header otherwise. */}
      <div className="card" data-owner="p" style={{ marginBottom: 14 }}>
        <div className="panel-head-row">
          <h3 style={{ margin: 0 }}>{t("well.events")}</h3>
          {events.length > 0 ? <NewEventButton wellUwi={well.uwi} /> : null}
        </div>
        {events.length === 0 ? (
          <NewEventButton wellUwi={well.uwi} variant="empty" />
        ) : (
          <div className="event-chips" role="group" aria-label={t("well.events")}>
            {events.map((ev) => (
              <button
                key={ev.id}
                type="button"
                className={`event-chip${ev.id === activeEvent?.id ? " active" : ""}`}
                onClick={() => dispatch(setActiveEvent(ev.id))}
              >
                <span className="event-code">{ev.event_code ?? "—"}</span>
                {t(eventTypeKey(ev))}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Doorway cards — one real Oxbo-P stat each */}
      {activeEvent ? (
        <div className="doorways">
          <DailyDoorway event={activeEvent} onOpen={() => navigate("/daily")} />
          <CostDoorway event={activeEvent} onOpen={() => navigate("/cost-afe")} />
          <NptDoorway event={activeEvent} onOpen={() => navigate("/time-npt")} />
        </div>
      ) : null}
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className={`kv-v${mono ? " mono" : ""}`}>{v}</span>
    </div>
  );
}

/** Parse a coordinate input → number, or null when blank; NaN → undefined (invalid). */
function parseCoord(raw: string): number | null | undefined {
  const s = raw.trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * "Ubicación" section of the Ficha del pozo. Shows the well's current lat/lon
 * (from Oxbo-M's `properties`, carried on the WellView). For a global admin it
 * exposes editable numeric inputs + a Guardar button that PATCHes the well's
 * coordinates on Oxbo-M — the fix Ramon uses to correct a mis-plotted well
 * (e.g. one sitting at lat 79.7 / lon 28.5). Non-admins see read-only values.
 * A 403 surfaces "Solo administradores"; a client-side range check pre-empts the
 * server's 422.
 */
function LocationCard({ well }: { well: WellView }) {
  const { t } = useLocale();
  const admin = isGlobalAdmin();

  const fmt = (v: number | null) => (v == null ? t("well.location.none") : String(v));

  if (!admin) {
    return (
      <div className="card" data-owner="m" style={{ marginBottom: 14 }}>
        <h3>{t("well.location.title")}</h3>
        <div className="kv-grid">
          <KV k={t("well.location.lat")} v={fmt(well.lat)} mono />
          <KV k={t("well.location.lon")} v={fmt(well.lon)} mono />
        </div>
      </div>
    );
  }

  return <LocationEditor well={well} />;
}

/** The admin-only editable form for a well's coordinates. */
function LocationEditor({ well }: { well: WellView }) {
  const { t } = useLocale();
  const [updateCoords, { isLoading, isSuccess, error, reset }] = useUpdateWellCoordsMutation();

  const [lat, setLat] = useState(well.lat == null ? "" : String(well.lat));
  const [lon, setLon] = useState(well.lon == null ? "" : String(well.lon));
  const [rangeError, setRangeError] = useState(false);

  const onEdit = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setRangeError(false);
    if (isSuccess || error) reset();
  };

  const save = () => {
    const latV = parseCoord(lat);
    const lonV = parseCoord(lon);
    // Client-side range guard (mirrors the server's 422) so bad input never ships.
    const badLat = latV === undefined || (latV != null && (latV < -90 || latV > 90));
    const badLon = lonV === undefined || (lonV != null && (lonV < -180 || lonV > 180));
    if (badLat || badLon) {
      setRangeError(true);
      return;
    }
    void updateCoords({ id: well.id, lat: latV ?? null, lon: lonV ?? null });
  };

  const forbidden = isForbidden(error);

  return (
    <div className="card" data-owner="m" style={{ marginBottom: 14 }}>
      <h3>{t("well.location.title")}</h3>
      <div className="loc-form">
        <label className="loc-field">
          <span className="kv-k">{t("well.location.lat")}</span>
          <input
            className="pick-input mono"
            type="number"
            step="any"
            inputMode="decimal"
            value={lat}
            onChange={(ev) => onEdit(setLat)(ev.target.value)}
            aria-label={t("well.location.lat")}
          />
        </label>
        <label className="loc-field">
          <span className="kv-k">{t("well.location.lon")}</span>
          <input
            className="pick-input mono"
            type="number"
            step="any"
            inputMode="decimal"
            value={lon}
            onChange={(ev) => onEdit(setLon)(ev.target.value)}
            aria-label={t("well.location.lon")}
          />
        </label>
        <button type="button" className="btn primary" disabled={isLoading} onClick={save}>
          {isLoading ? t("well.location.saving") : t("well.location.save")}
        </button>
      </div>
      <p className="loc-note" style={{ color: "var(--dim)", marginTop: 8 }}>
        {t("well.location.note")}
      </p>
      {rangeError ? <div className="pick-error">{t("well.location.rangeError")}</div> : null}
      {error ? (
        <div className="pick-error">
          {forbidden ? t("well.location.forbidden") : t("well.location.error")}
        </div>
      ) : null}
      {isSuccess && !rangeError ? (
        <div className="loc-saved" style={{ color: "var(--ok, #2e7d32)", marginTop: 6 }}>
          {t("well.location.saved")}
        </div>
      ) : null}
    </div>
  );
}

/** Daily-report doorway: shows the latest report day (from P). */
function DailyDoorway({ event, onOpen }: { event: EventOut; onOpen: () => void }) {
  const { t } = useLocale();
  const { data: reports = [] } = useGetEventReportsQuery(event.id);
  const latest = reports[0]; // handlers return newest-first
  return (
    <button type="button" className="doorway" data-owner="p" onClick={onOpen}>
      <span className="doorway-title">{t("well.doorway.dailyReport")}</span>
      <span className="doorway-stat">
        {latest?.report_no != null
          ? t("well.doorway.latestReport", { n: latest.report_no })
          : t("common.na")}
      </span>
      <span className="doorway-open">{t("well.doorway.open")} →</span>
    </button>
  );
}

/**
 * Cost doorway: the "Costo y AFE" card. Shows the authorized AFE total (the
 * budget the job was sanctioned for) as the headline figure, with % of AFE
 * spent-to-date underneath — both from P's plan-vs-actual roll-up. Falls back to
 * N/A only when the event carries no AFE budget yet.
 */
function CostDoorway({ event, onOpen }: { event: EventOut; onOpen: () => void }) {
  const { t, locale } = useLocale();
  const { data } = useGetPlanVsActualQuery(event.id);
  const hasAfe = !!data && data.afe_total > 0;
  const pct = hasAfe ? (data.actual_total / data.afe_total) * 100 : null;
  return (
    <button type="button" className="doorway" data-owner="p" onClick={onOpen}>
      <span className="doorway-title">{t("well.doorway.costAfe")}</span>
      <span className="doorway-stat">
        {hasAfe
          ? t("well.doorway.afeAuthorized", {
              amount: fmtMoney(data.afe_total, locale, { compact: true }),
            })
          : t("common.na")}
      </span>
      {pct != null ? (
        <span className="doorway-sub">{t("well.doorway.spent", { pct: fmtPct(pct, locale) })}</span>
      ) : null}
      <span className="doorway-open">{t("well.doorway.open")} →</span>
    </button>
  );
}

/** NPT doorway: shows non-productive-time % (from P npt-rollup). */
function NptDoorway({ event, onOpen }: { event: EventOut; onOpen: () => void }) {
  const { t, locale } = useLocale();
  const { data } = useGetNptRollupQuery(event.id);
  return (
    <button type="button" className="doorway" data-owner="p" onClick={onOpen}>
      <span className="doorway-title">{t("well.doorway.timeNpt")}</span>
      <span className="doorway-stat">
        {data?.npt_pct != null
          ? t("well.doorway.nptPct", { pct: fmtPct(data.npt_pct, locale, 1) })
          : t("common.na")}
      </span>
      <span className="doorway-open">{t("well.doorway.open")} →</span>
    </button>
  );
}
